// Utility to update all SEO meta tags for blog pages
// Ensures Googlebot sees correct canonical, og:url, og:title, og:description, twitter:title

import { useLayoutEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

export function updateSEOMeta({ title, description, url, lang }) {
  document.title = title;

  const setMeta = (selector, attr, value) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };

  setMeta('meta[name="description"]', 'content', description);
  setMeta('meta[property="og:title"]', 'content', title);
  setMeta('meta[property="og:description"]', 'content', description);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('meta[name="twitter:title"]', 'content', title);

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', url);

  if (lang) document.documentElement.lang = lang;
}

export function resetSEOMeta() {
  const homeUrl = 'https://mareariviera.com';
  const homeTitle = 'Marea Riviera | Inversión Inmobiliaria en Riviera Maya';
  const homeDesc = 'Invierte en propiedades en Tulum, Playa del Carmen y Puerto Morelos con certeza jurídica, ROI 8-15% anual y cash flow desde el primer mes. Más de 641 desarrollos con MIA vigente. Marea Riviera.';

  document.title = homeTitle;

  const setMeta = (selector, attr, value) => {
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
  };

  setMeta('meta[name="description"]', 'content', homeDesc);
  setMeta('meta[property="og:title"]', 'content', 'Marea Riviera | Inversión Inmobiliaria Riviera Maya');
  setMeta('meta[property="og:description"]', 'content', 'Propiedades que se pagan solas. Cash flow desde el primer mes. Certeza jurídica absoluta.');
  setMeta('meta[property="og:url"]', 'content', homeUrl);
  setMeta('meta[name="twitter:title"]', 'content', 'Marea Riviera | Inversión Inmobiliaria Riviera Maya');

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', homeUrl);
}

/**
 * Hook that forces a blog's default language BEFORE first paint (no flash).
 * Saves the previous language and restores it on unmount.
 * Also sets <html lang="..."> attribute.
 */
export function useBlogLanguage(defaultLang) {
  const { language, setLanguage } = useLanguage();
  const prevLangRef = useRef(null);

  useLayoutEffect(() => {
    prevLangRef.current = language;
    setLanguage(defaultLang);
    document.documentElement.lang = defaultLang;

    return () => {
      const restore = prevLangRef.current || 'es';
      setLanguage(restore);
      document.documentElement.lang = restore;
    };
  }, []); // Only on mount/unmount

  // Keep <html lang> in sync if user toggles manually
  useLayoutEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
}
